'use client';

import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { commercialLoginTarget, withCommercialLoginTimeout } from '@/lib/client/commercial-login';
import Link from 'next/link';

// Roles allowed in the commercial space
const COMMERCIAL_ROLES = ['owner', 'admin', 'manager', 'commercial', 'production'] as const;
type CommercialRole = (typeof COMMERCIAL_ROLES)[number];

type LoginState =
  | 'idle' |'submitting' |'resolving_role' |'redirecting' |'wrong_space_client' |'role_unsupported' |'auth_failed' |'role_lookup_failed';

function LoginForm() {
  const searchParams = useSearchParams();
  const nextPath = commercialLoginTarget(searchParams.get('next'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginState, setLoginState] = useState<LoginState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const submitting = useRef(false);
  const attempt = useRef(0);
  const clientRedirect = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openSpace = useCallback((path: string) => {
    setOpeningPath(path);
    setLoginState('redirecting');
    // A new document uses the session cookies just written by Supabase. A cached
    // anonymous router response must not send a successful login back to this form.
    window.location.replace(path);
  }, []);

  // ── Session check on mount ────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    async function checkSession() {
      try {
        const supabase = createClient();
        const { data: { session }, error: sessionError } = await withCommercialLoginTimeout(supabase.auth.getSession());
        if (!active) return;
        if (sessionError) throw sessionError;
        if (session?.user) {
          // Resolve role before redirecting
          const { data: memberData, error: memberError } = await withCommercialLoginTimeout(supabase
            .from('organization_members')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('status', 'active')
            .maybeSingle());
          if (!active) return;
          if (memberError) throw new Error('ROLE_LOOKUP_FAILED');

          const role = memberData?.role as string | undefined;

          if (role && (COMMERCIAL_ROLES as readonly string[]).includes(role)) {
            openSpace(nextPath);
          }
          // Not a commercial role — fall through to show the form
        }
      } catch {
        if (active) setError('La session n’a pas pu être vérifiée. Vous pouvez réessayer de vous connecter.');
      } finally {
        if (active) setCheckingSession(false);
      }
    }
    void checkSession();
    return () => {
      active = false;
      attempt.current++;
      if (clientRedirect.current) clearTimeout(clientRedirect.current);
    };
  }, [nextPath, openSpace]);

  // ── Open the authenticated client's own space ────────────────────────
  function handleGoToClientSpace() {
    if (clientRedirect.current) clearTimeout(clientRedirect.current);
    openSpace('/client/dashboard');
  }

  // ── Handle "change account" ───────────────────────────────────────────
  async function handleChangeAccount() {
    attempt.current++;
    if (clientRedirect.current) clearTimeout(clientRedirect.current);
    try {
      const supabase = createClient();
      const { error: signOutError } = await withCommercialLoginTimeout(supabase.auth.signOut({ scope: 'global' }));
      if (signOutError) throw signOutError;
      setLoginState('idle');
      setError(null);
      setOpeningPath(null);
      setEmail('');
      setPassword('');
    } catch {
      setError('Le changement de compte n’a pas abouti. Veuillez réessayer.');
      setLoginState('auth_failed');
    } finally {
      submitting.current = false;
    }
  }

  // ── Form submit ───────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    const currentAttempt = ++attempt.current;
    setError(null);
    setLoginState('submitting');

    try {
      const supabase = createClient();
      // BRANCH: AUTH
      const { data, error: signInError } = await withCommercialLoginTimeout(supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      }));
      if (currentAttempt !== attempt.current) return;

      if (signInError) {
        setError(
          signInError.message === 'Invalid login credentials' ?'Email ou mot de passe incorrect.'
            : signInError.message
        );
        setLoginState('auth_failed');
        return;
      }

      if (!data.user) {
        setError('Connexion échouée — aucun utilisateur retourné. Veuillez réessayer.');
        setLoginState('auth_failed');
        return;
      }

      // BRANCH: ROLE_LOOKUP
      setLoginState('resolving_role');

      const { data: memberData, error: memberError } = await withCommercialLoginTimeout(supabase
        .from('organization_members')
        .select('role')
        .eq('user_id', data.user.id)
        .eq('status', 'active')
        .maybeSingle());
      if (currentAttempt !== attempt.current) return;

      if (memberError) {
        setError('Impossible de vérifier votre rôle. Veuillez réessayer.');
        setLoginState('role_lookup_failed');
        return;
      }

      const role = memberData?.role as string | undefined;

      // BRANCH: ROLE_CLIENT — redirect to client space
      if (role === 'client') {
        setLoginState('wrong_space_client');
        // Auto-redirect after a brief moment so the message is visible
        clientRedirect.current = setTimeout(() => {
          if (currentAttempt === attempt.current) openSpace('/client/dashboard');
        }, 2500);
        return;
      }

      // BRANCH: ROLE_INTERNAL (commercial roles)
      if (role && (COMMERCIAL_ROLES as readonly string[]).includes(role)) {
        openSpace(nextPath);
        return;
      }

      // BRANCH: ROLE_UNSUPPORTED
      setError("Votre compte n'est associé à aucun espace actif.");
      setLoginState('role_unsupported');
    } catch (err: unknown) {
      if (currentAttempt !== attempt.current) return;
      setError(err instanceof Error && err.message === 'LOGIN_TIMEOUT'
        ? 'La connexion prend trop de temps. Veuillez réessayer.'
        : 'La connexion a été interrompue. Veuillez réessayer.');
      setLoginState('auth_failed');
    } finally {
      if (currentAttempt === attempt.current) submitting.current = false;
    }
  }

  // ── Loading: checking existing session ───────────────────────────────
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#00d4c8]/30 border-t-[#00d4c8] rounded-full animate-spin" />
      </div>
    );
  }

  // ── Wrong space: client account tried commercial login ────────────────
  if (loginState === 'wrong_space_client') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-[#00d4c8]/20 border border-[#00d4c8]/40 flex items-center justify-center">
              <span className="text-[#00d4c8] text-xs font-bold">R</span>
            </div>
            <span className="text-white font-semibold tracking-wide">RDV-PARTNERS</span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-6">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-white font-semibold text-lg mb-2">Ce compte appartient à l'espace client</h2>
            <p className="text-gray-400 text-sm mb-1">
              Authentification réussie — redirection vers votre espace client…
            </p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
              <span className="text-amber-400 text-xs">Redirection en cours…</span>
            </div>
          </div>

          <button
            onClick={handleGoToClientSpace}
            className="w-full py-3 px-6 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-500/90 transition-all text-sm tracking-wide mb-3"
          >
            Accéder à mon espace client maintenant
          </button>

          <button
            onClick={handleChangeAccount}
            className="w-full py-3 px-6 bg-white/5 border border-white/10 text-gray-400 font-medium rounded-lg hover:bg-white/10 transition-all text-sm"
          >
            Changer de compte
          </button>
        </div>
      </div>
    );
  }

  const isLoading = loginState === 'submitting' || loginState === 'resolving_role' || loginState === 'redirecting';

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-lg bg-[#00d4c8]/20 border border-[#00d4c8]/40 flex items-center justify-center">
              <span className="text-[#00d4c8] text-xs font-bold">R</span>
            </div>
            <span className="text-white font-semibold tracking-wide">RDV-PARTNERS</span>
          </div>
          <h1 className="text-2xl font-light text-white tracking-wide">Espace commercial</h1>
          <p className="text-gray-500 text-sm mt-2">Connectez-vous pour accéder à l'Architecte commercial</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-gray-400 mb-2 tracking-wider uppercase">
              Adresse email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="contact@rdvpartners.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-[#00d4c8]/50 focus:bg-white/8 transition-all"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-medium text-gray-400 mb-2 tracking-wider uppercase">
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-[#00d4c8]/50 focus:bg-white/8 transition-all"
            />
            <div className="mt-2 text-right">
              <Link href="/auth/forgot-password" className="text-gray-500 hover:text-[#00d4c8] text-xs transition-colors">
                Mot de passe oublié ?
              </Link>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !email || !password}
            className="w-full py-3 px-6 bg-[#00d4c8] text-[#0a0a0f] font-semibold rounded-lg hover:bg-[#00d4c8]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-[#0a0a0f]/30 border-t-[#0a0a0f] rounded-full animate-spin" />
                {loginState === 'resolving_role' ? 'Vérification de votre accès…' : loginState === 'redirecting' ? 'Ouverture de votre espace…' : 'Connexion en cours…'}
              </span>
            ) : (
              'Se connecter'
            )}
          </button>
          {loginState === 'redirecting' && openingPath && <p role="status" className="text-center text-sm text-white/80">Connexion réussie. <a href={openingPath} className="text-[#67e8d9] underline underline-offset-4">Ouvrir mon espace</a></p>}
        </form>

        {/* Cross-link to client space */}
        <div className="mt-6 p-4 bg-white/3 border border-white/8 rounded-lg text-center">
          <p className="text-gray-500 text-xs mb-2">Vous êtes client ?</p>
          <Link
            href="/client/login"
            className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Accéder à l'espace client
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center space-y-2">
          <Link href="/" className="text-gray-600 hover:text-gray-400 text-xs transition-colors block">
            ← Retour au site
          </Link>
          <Link href="/commercial/auth-diagnostic" className="text-gray-700 hover:text-gray-500 text-xs transition-colors block">
            Diagnostic d'authentification
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function CommercialLoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#00d4c8]/30 border-t-[#00d4c8] rounded-full animate-spin" />
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
